/**
 * Simply Steaks - frame-sequence player for phones.
 *
 * Scrubbing a <video> needs the whole file first and is unreliable on phones
 * (iOS refuses to paint seeked frames, slow networks leave the poster up for
 * minutes). This player draws pre-rendered frames onto a <canvas> instead:
 *
 * - Frames are packed into a few WebP "sheets" (cols x rows grid each).
 * - Sheets are ordered coarse-to-fine: sheet 0 holds every 8th frame across
 *   the WHOLE clip, later sheets fill in the gaps. For the hero this means
 *   it works after the first ~150 KB and gets smoother as more arrives; for
 *   everything else (`smooth: true`) nothing is drawn until a couple of
 *   sheets are in, so scrubbing never jumps across a still-unloaded stretch
 *   of the clip and never looks stuck.
 * - Each sheet is cut into one small canvas per frame and the big image is
 *   dropped, so phones never re-decode a multi-megapixel sheet mid-scroll.
 * - Between two loaded frames the player cross-fades.
 *
 * The frame order below must match the one used when the sheets were built.
 *
 * Usage: var clip = __ssFrames.create({ metaUrl, canvas, host, mount,
 *   position: [x, y], rank, onFirstFrame, onComplete, onFail });
 *   clip.start(); then call clip.update(progress 0..1, dtSeconds) every
 *   animation frame. `host` is the element whose box the canvas fills; the
 *   canvas is added to the page through mount(canvas) once it has a picture.
 */
(function () {
  'use strict';

  if (window.__ssFrames) return;

  var MAX_PARALLEL = 3;
  var SHEET_TIMEOUT_MS = 30000;
  var MAX_TRIES = 4;
  // Short on purpose: long easing makes the picture trail behind the finger.
  var SMOOTHING_SECONDS = 0.03;

  var clips = [];
  var running = 0;
  var pumpTimer = 0;

  // Cutting sheets into per-frame canvases is spread over animation frames
  // (a few ms each) so it never causes a hitch while the user is scrolling.
  var cutQueue = [];
  var cutBusy = false;
  var CUT_BUDGET_MS = 2;
  function runCuts() {
    var started = performance.now();
    while (cutQueue.length && performance.now() - started < CUT_BUDGET_MS) {
      cutQueue.shift()();
    }
    if (cutQueue.length) {
      window.requestAnimationFrame(runCuts);
    } else {
      cutBusy = false;
    }
  }
  function queueCut(job) {
    cutQueue.push(job);
    if (!cutBusy) {
      cutBusy = true;
      window.requestAnimationFrame(runCuts);
    }
  }

  function order(n) {
    var seen = {};
    var out = [];
    [8, 4, 2, 1].forEach(function (stride) {
      var list = [];
      for (var i = 0; i < n; i += stride) list.push(i);
      if (stride === 8 && list[list.length - 1] !== n - 1) list.push(n - 1);
      list.forEach(function (index) {
        if (!seen[index]) {
          seen[index] = true;
          out.push(index);
        }
      });
    });
    return out;
  }

  function clamp01(value) {
    return Math.min(1, Math.max(0, value));
  }

  /* --------------------------------------------------------- scheduler */
  // Every clip advances one sheet at a time, always picking the clip that is
  // proportionally furthest behind, so all clips get their coarse pass first.
  function pump() {
    window.clearTimeout(pumpTimer);
    var now = Date.now();
    var soonest = 0;

    // The first clip (the hero) gets the whole connection until it has a few
    // sheets and is animating; only then do the other clips start, and the
    // very first sheet is fetched alone so it arrives as fast as possible.
    var lead = null;
    var anyLoaded = false;
    for (var c = 0; c < clips.length; c += 1) {
      if (clips[c].dead) continue;
      if (clips[c].rank === 0) lead = clips[c];
      if (clips[c].loadedCount > 0) anyLoaded = true;
    }
    var leadHolding = !!lead && (!lead.meta || lead.loadedCount < lead.meta.sheets);
    var limit = anyLoaded ? MAX_PARALLEL : 1;

    while (running < limit) {
      var best = null;
      var bestSheet = -1;
      var bestKey = Infinity;
      for (var i = 0; i < clips.length; i += 1) {
        var clip = clips[i];
        if (clip.dead || !clip.meta) continue;
        if (leadHolding && clip !== lead) continue;
        var pick = clip.pickSheet(now);
        if (pick.wait) {
          soonest = soonest ? Math.min(soonest, pick.wait) : pick.wait;
        }
        if (pick.sheet < 0) continue;
        var key = pick.sheet / clip.meta.sheets + clip.rank * 0.001;
        if (key < bestKey) {
          bestKey = key;
          best = clip;
          bestSheet = pick.sheet;
        }
      }
      if (!best) break;
      best.loadSheet(bestSheet);
    }
    if (soonest && running < MAX_PARALLEL) {
      pumpTimer = window.setTimeout(pump, Math.max(soonest - now, 50));
    }
  }

  /* -------------------------------------------------------------- clip */
  function create(opts) {
    var canvas = opts.canvas;
    var host = opts.host;
    var ctx = null;
    var mounted = false;
    var position = opts.position || [0.5, 0.5];

    var clip = {
      rank: opts.rank || 0,
      meta: null,
      loadedCount: 0,
      // The lead clip (the hero) always loads everything. Others load only
      // their coarse first sheet until they are close to the viewport.
      active: !opts.rank,
      dead: false,
      pickSheet: pickSheet,
      loadSheet: loadSheet
    };

    var slots = null; // frame index -> { sheet, x, y }
    var has = []; // frame index -> true once its canvas exists
    var pieces = []; // frame index -> <canvas>
    var anyFrame = false;
    // Short clips (the featured products, not the hero) hold off painting
    // anything until a couple of sheets are in, instead of revealing sheet 0
    // alone: with only ~40% of the frames in, a quick drag jumps across real
    // gaps in the footage and looks stuck/stepped rather than smooth. The
    // clip is small, so waiting barely delays the first picture.
    var smooth = !!opts.smooth;
    var revealAt = 1;
    var sheets = []; // { img, state, tries, retryAt, timer }
    var current = 0;
    var lastDrawn = -1;
    var dirty = true;
    var completed = false;
    var canvasW = 0;
    var canvasH = 0;
    var pendingW = 0;
    var pendingH = 0;
    var base = '';
    var query = '';
    var observer = null;

    function sheetUrl(index) {
      return base + '-' + index + '.webp' + query;
    }

    function fail() {
      if (clip.dead) return;
      clip.destroy();
      if (opts.onFail) opts.onFail();
    }

    function start() {
      var url = opts.metaUrl;
      var q = url.indexOf('?');
      query = q >= 0 ? url.slice(q) : '';
      base = (q >= 0 ? url.slice(0, q) : url).replace(/\.json$/, '');
      clips.push(clip);

      // Sheet 0 does not depend on the metadata, so the lead clip starts
      // downloading it right away instead of waiting a round trip.
      if (clip.rank === 0) {
        var head = new Image();
        head.decoding = 'async';
        head.src = sheetUrl(0);
      }

      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('meta ' + response.status);
          return response.json();
        })
        .then(function (meta) {
          if (clip.dead) return;
          meta.per = meta.cols * meta.rows;
          meta.sheets = Math.ceil(meta.n / meta.per);
          clip.meta = meta;
          revealAt = smooth ? Math.min(2, meta.sheets) : 1;
          slots = [];
          order(meta.n).forEach(function (frame, j) {
            var k = j % meta.per;
            slots[frame] = { sheet: Math.floor(j / meta.per), x: (k % meta.cols) * meta.w, y: Math.floor(k / meta.cols) * meta.h };
          });
          for (var s = 0; s < meta.sheets; s += 1) {
            sheets.push({ img: null, state: 0, tries: 0, retryAt: 0, timer: 0 });
          }
          pump();
        })
        .catch(fail);

      if (host && typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(measure);
        observer.observe(host);
      }
      window.addEventListener('resize', measure);
    }

    function pickSheet(now) {
      var wait = 0;
      for (var s = 0; s < sheets.length; s += 1) {
        var sheet = sheets[s];
        if (sheet.state !== 0) continue;
        if (s > 0 && !clip.active) break;
        if (sheet.retryAt > now) {
          wait = wait ? Math.min(wait, sheet.retryAt) : sheet.retryAt;
          continue;
        }
        return { sheet: s, wait: wait };
      }
      return { sheet: -1, wait: wait };
    }

    function loadSheet(index) {
      var sheet = sheets[index];
      var img = new Image();
      img.decoding = 'async';
      sheet.state = 1;
      sheet.img = img;
      running += 1;
      var settled = false;

      function settle(ok) {
        if (settled) return;
        settled = true;
        window.clearTimeout(sheet.timer);
        running -= 1;
        if (clip.dead) return;
        if (ok) {
          sheet.state = 2;
          registerSheet(index);
        } else {
          sheet.img = null;
          sheet.state = 0;
          sheet.tries += 1;
          if (sheet.tries >= MAX_TRIES) {
            sheet.state = 3;
            if (index === 0) fail();
          } else {
            sheet.retryAt = Date.now() + 1200 * sheet.tries;
          }
        }
        pump();
      }

      img.onload = function () {
        if (img.decode) {
          img.decode().then(
            function () {
              settle(true);
            },
            function () {
              settle(true); // decode() can reject for cached images; drawImage still works
            }
          );
        } else {
          settle(true);
        }
      };
      img.onerror = function () {
        settle(false);
      };
      sheet.timer = window.setTimeout(function () {
        img.onload = img.onerror = null;
        img.src = '';
        settle(false);
      }, SHEET_TIMEOUT_MS);
      img.src = sheetUrl(index);
    }

    // Each frame gets its own small canvas and the big sheet image is dropped.
    // Canvases keep their pixels resident, so phones never have to re-decode a
    // multi-megapixel sheet in the middle of a scroll.
    function registerSheet(index) {
      var meta = clip.meta;
      var sheet = sheets[index];
      var img = sheet.img;
      var todo = [];
      for (var frame = 0; frame < meta.n; frame += 1) {
        if (slots[frame].sheet === index) todo.push(frame);
      }
      var remaining = todo.length;
      todo.forEach(function (frame) {
        queueCut(function () {
          if (clip.dead) return;
          var slot = slots[frame];
          var piece = document.createElement('canvas');
          piece.width = meta.w - 2;
          piece.height = meta.h - 2;
          // Inset by 1px so filtering never bleeds in the neighbouring frame.
          piece.getContext('2d').drawImage(img, slot.x + 1, slot.y + 1, piece.width, piece.height, 0, 0, piece.width, piece.height);
          pieces[frame] = piece;
          has[frame] = true;
          if (!smooth) anyFrame = true;
          dirty = true;
          remaining -= 1;
          if (remaining === 0) {
            sheet.img = null;
            clip.loadedCount += 1;
            if (clip.loadedCount >= revealAt) anyFrame = true;
            pump();
            if (!completed && clip.loadedCount === sheets.length) {
              completed = true;
              if (opts.onComplete) opts.onComplete();
            }
          }
        });
      });
    }

    /* ------------------------------------------------------------ drawing */
    // The canvas box is measured by the observer, never inside the animation
    // loop, so drawing never forces a layout.
    function measure() {
      if (!host) return;
      var rect = host.getBoundingClientRect();
      pendingW = rect.width;
      pendingH = rect.height;
      dirty = true;
    }

    function applySize() {
      if (!pendingW || !pendingH) return false;
      var ratio = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.round(pendingW * ratio);
      var h = Math.round(pendingH * ratio);
      // Ignore tiny changes (mobile toolbars): resizing a canvas clears it.
      if (!canvasW || Math.abs(w - canvasW) > 2 || Math.abs(h - canvasH) > 2) {
        canvasW = canvas.width = w;
        canvasH = canvas.height = h;
        dirty = true;
      }
      return true;
    }

    function drawFrame(frame) {
      var meta = clip.meta;
      var piece = pieces[frame];
      if (!piece) return;
      var scale = Math.max(canvasW / piece.width, canvasH / piece.height);
      var dw = piece.width * scale;
      var dh = piece.height * scale;
      ctx.drawImage(piece, (canvasW - dw) * position[0], (canvasH - dh) * position[1], dw, dh);
    }

    function draw(progress) {
      var meta = clip.meta;
      var pos = clamp01(progress) * (meta.n - 1);
      var lo = -1;
      var hi = -1;
      var i;
      for (i = Math.floor(pos); i >= 0; i -= 1) {
        if (has[i]) {
          lo = i;
          break;
        }
      }
      for (i = Math.ceil(pos); i < meta.n; i += 1) {
        if (has[i]) {
          hi = i;
          break;
        }
      }
      if (lo < 0 && hi < 0) return false;
      if (lo < 0) lo = hi;
      if (hi < 0) hi = lo;

      ctx.globalAlpha = 1;
      drawFrame(lo);
      if (hi !== lo) {
        var t = (pos - lo) / (hi - lo);
        if (t > 0.02) {
          ctx.globalAlpha = Math.min(t, 1);
          drawFrame(hi);
          ctx.globalAlpha = 1;
        }
      }
      return true;
    }

    clip.start = start;
    clip.update = function (target, dt) {
      if (!clip.meta || clip.dead || !anyFrame) return;
      var alpha = 1 - Math.exp(-(dt || 0.016) / SMOOTHING_SECONDS);
      current += (target - current) * alpha;
      if (Math.abs(target - current) < 0.0002) current = target;
      if (current === lastDrawn && !dirty) return;
      if (!pendingW) measure();
      if (!applySize()) return;
      if (!ctx) {
        // An opaque canvas is cheaper to composite. It only joins the page
        // once it has a picture, so it can never cover the poster with black.
        try {
          ctx = canvas.getContext('2d', { alpha: false, desynchronized: true });
        } catch (error) {
          ctx = null;
        }
        if (!ctx) ctx = canvas.getContext('2d');
        // Smoother scaling of the frames up to the screen size.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      }
      if (draw(current)) {
        lastDrawn = current;
        dirty = false;
        if (!mounted) {
          mounted = true;
          if (opts.mount) opts.mount(canvas);
          if (opts.onFirstFrame) opts.onFirstFrame();
        }
      }
    };
    clip.frameCount = function () {
      return clip.meta ? clip.meta.n : 0;
    };
    clip.resize = measure;
    clip.setActive = function (value) {
      if (value && !clip.active) {
        clip.active = true;
        pump();
      }
    };
    clip.destroy = function () {
      clip.dead = true;
      sheets.forEach(function (sheet) {
        window.clearTimeout(sheet.timer);
        if (sheet.img) sheet.img.onload = sheet.img.onerror = null;
        sheet.img = null;
      });
      has = [];
      pieces = [];
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
      var at = clips.indexOf(clip);
      if (at >= 0) clips.splice(at, 1);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };

    return clip;
  }

  window.__ssFrames = { create: create, order: order };
})();
